import { useCallback, useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MapPinned, ChevronUp, ChevronDown, LogOut } from 'lucide-react';
import clsx from 'clsx';
import { APIError, fetchMyCampaignAssignments, fetchMyAssignedZones, type MyCampaignAssignmentRow, type MyAssignedZoneRow } from '../../services/api';

interface Props {
  isAuthenticated: boolean;
  isHeroMode: boolean;
  /** Hide when the map is already focused on a campaign (?campaign_id=). */
  suppressed?: boolean;
  /** When false, user has no map-visible campaigns (or cannot access the list) — hide launcher. */
  assignmentsEligible?: boolean;
  /** Increment from Profile / Settings to open this picker (main map only). */
  expandNonce?: number;
  /** Increment to open the assignments list without hiding explore chrome (e.g. map control). */
  listOpenOnlyNonce?: number;
  /** Explore chrome (bottom sheet + map toggle) is hidden until Exit — show Exit and use compact anchor. */
  exploreChromeHidden?: boolean;
  /** `false` when bottom sheet is hidden — dock launcher lower on the screen. */
  anchorAboveBottomSheet?: boolean;
  /** Call when user opens the assignments list (engage “field campaigns” surface). */
  onEngageFieldCampaigns?: () => void;
  /** User tapped Exit — parent shows confirmation before restoring chrome. */
  onRequestExitFieldCampaigns?: () => void;
  /** Increment after confirmed exit — collapses the list without re-showing chrome. */
  listCollapseKey?: number;
  /** Open campaign workspace; pass zoneId to deep-link selection. */
  onOpenCampaign: (campaignId: string, zoneId?: string | null) => void;
}

export function MyCampaignsLauncher({
  isAuthenticated,
  isHeroMode,
  suppressed = false,
  assignmentsEligible = true,
  expandNonce = 0,
  listOpenOnlyNonce = 0,
  exploreChromeHidden = false,
  anchorAboveBottomSheet = true,
  onEngageFieldCampaigns,
  onRequestExitFieldCampaigns,
  listCollapseKey = 0,
  onOpenCampaign,
}: Props) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<MyCampaignAssignmentRow[]>([]);
  const [assignedZoneRows, setAssignedZoneRows] = useState<MyAssignedZoneRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastExpandNonce = useRef(0);
  const lastListOpenOnlyNonce = useRef(0);
  const lastCollapseKey = useRef(0);

  useEffect(() => {
    if (listCollapseKey > lastCollapseKey.current) {
      lastCollapseKey.current = listCollapseKey;
      setOpen(false);
    }
  }, [listCollapseKey]);

  useEffect(() => {
    if (expandNonce > lastExpandNonce.current) {
      lastExpandNonce.current = expandNonce;
      setOpen(true);
      onEngageFieldCampaigns?.();
    }
  }, [expandNonce, onEngageFieldCampaigns]);

  useEffect(() => {
    if (listOpenOnlyNonce > lastListOpenOnlyNonce.current) {
      lastListOpenOnlyNonce.current = listOpenOnlyNonce;
      setOpen(true);
      onEngageFieldCampaigns?.();
    }
  }, [listOpenOnlyNonce, onEngageFieldCampaigns]);

  const toggleList = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) onEngageFieldCampaigns?.();
      return next;
    });
  }, [onEngageFieldCampaigns]);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const [list, zones] = await Promise.all([
        fetchMyCampaignAssignments(),
        fetchMyAssignedZones(),
      ]);
      setRows(list);
      setAssignedZoneRows(zones);
    } catch (e) {
      if (e instanceof APIError && e.status === 401) {
        setError('Sign in to list your campaigns.');
      } else {
        setError(e instanceof Error ? e.message : 'Could not load campaigns');
      }
      setRows([]);
      setAssignedZoneRows([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!open || !isAuthenticated) return;
    void load();
  }, [open, isAuthenticated, load]);

  if (!isAuthenticated || isHeroMode || suppressed || !assignmentsEligible) return null;

  // Explore map: no floating “Field assignments” card until the list is opened or field mode is engaged.
  if (!exploreChromeHidden && !open) return null;

  return (
    <motion.div
      className={clsx(
        'absolute z-20 left-3 right-3 md:left-auto md:right-4 md:w-[380px]',
        'max-h-[min(50vh,380px)] flex flex-col rounded-[1.1rem] overflow-hidden',
        'border border-slate-200/95 bg-white/98 backdrop-blur-xl shadow-lg shadow-slate-900/12',
        'dark:border-gray-600/55 dark:bg-gray-900/97 dark:shadow-black/45',
        'ring-1 ring-slate-200/70 dark:ring-gray-500/25',
        anchorAboveBottomSheet ? 'bottom-[5.5rem] md:bottom-8' : 'bottom-5 md:bottom-6',
      )}
      initial={{ opacity: 0, y: 18, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', damping: 30, stiffness: 340 }}
    >
      <div className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-90 dark:opacity-100 bg-[linear-gradient(135deg,rgba(6,182,212,0.08)_0%,rgba(16,185,129,0.06)_45%,transparent_75%)] dark:bg-[linear-gradient(135deg,rgba(6,182,212,0.14)_0%,rgba(16,185,129,0.1)_40%,transparent_72%)]"
          aria-hidden
        />
        <div className="relative">
          <div className="flex items-stretch gap-1.5 p-2">
            {exploreChromeHidden && onRequestExitFieldCampaigns ? (
              <button
                type="button"
                onClick={onRequestExitFieldCampaigns}
                className="flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl border border-rose-200/90 bg-rose-50/95 px-2.5 py-2 text-[10px] font-bold uppercase tracking-wide text-rose-800 shadow-sm hover:bg-rose-100/95 dark:border-rose-500/35 dark:bg-rose-950/40 dark:text-rose-100 dark:hover:bg-rose-950/55"
                title="Leave field campaigns (confirm)"
              >
                <LogOut className="h-3.5 w-3.5" />
                Exit
              </button>
            ) : null}
            <button
              type="button"
              onClick={toggleList}
              className={clsx(
                'flex min-w-0 flex-1 items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left transition-colors',
                'bg-slate-50/90 hover:bg-slate-100/95 dark:bg-gray-800/75 dark:hover:bg-gray-800/95',
                'border border-slate-200/90 dark:border-gray-600/45 shadow-sm',
              )}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-600 shadow-md shadow-cyan-900/25">
                  <MapPinned className="h-4 w-4 text-white" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 dark:text-gray-400">
                    Field assignments
                  </span>
                  <span className="block truncate text-sm font-semibold text-slate-900 dark:text-white">
                    My field campaigns
                  </span>
                </span>
              </span>
              {open ? (
                <ChevronUp className="h-4 w-4 shrink-0 text-slate-500 dark:text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 dark:text-gray-400" />
              )}
            </button>
          </div>
          <AnimatePresence initial={false}>
            {open ? (
              <motion.div
                key="field-assignments-list"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{
                  opacity: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
                  height: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
                }}
                className="overflow-hidden border-t border-slate-200/80 dark:border-gray-700/60"
              >
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    type: 'spring',
                    damping: 28,
                    stiffness: 380,
                    delay: 0.04,
                  }}
                  className="max-h-[min(44vh,300px)] overflow-y-auto px-2 pb-2 pt-1.5"
                >
                  {error && <p className="text-xs text-red-600 dark:text-red-300 px-1.5 py-2">{error}</p>}
                  {!loading && !error && rows.length === 0 && assignedZoneRows.length === 0 && (
                    <p className="text-xs text-slate-500 dark:text-gray-400 px-1.5 py-3 leading-relaxed">
                      No campaigns found for your organization yet. Create one in the Developer Console, or ask an admin
                      to add you to a campaign team.
                    </p>
                  )}
                  {loading && <p className="text-xs text-slate-500 dark:text-gray-400 px-1.5 py-3">Loading…</p>}
                  {assignedZoneRows.length > 0 ? (
                    <div className="px-1.5 pt-1 pb-2 border-b border-slate-200/70 dark:border-gray-700/50">
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-gray-400 px-0.5 mb-1">
                        Your zones
                      </p>
                      <p className="text-[10px] text-slate-500 dark:text-gray-400 px-0.5 mb-1.5 leading-snug">
                        Opens the campaign map with this zone selected so you can start a session.
                      </p>
                      <ul className="space-y-1">
                        {assignedZoneRows.map((z, i) => (
                          <motion.li
                            key={`az-${z.id}`}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                              type: 'spring',
                              damping: 26,
                              stiffness: 400,
                              delay: Math.min(i * 0.04, 0.32),
                            }}
                          >
                            <button
                              type="button"
                              disabled={!z.campaign_id}
                              onClick={() => {
                                if (!z.campaign_id) return;
                                onOpenCampaign(z.campaign_id, z.id);
                                setOpen(false);
                              }}
                              className="w-full text-left rounded-xl px-3 py-2 text-sm bg-cyan-50/90 dark:bg-cyan-950/35 hover:bg-cyan-100/95 dark:hover:bg-cyan-900/45 border border-cyan-200/80 dark:border-cyan-800/50 shadow-sm transition-colors active:scale-[0.99] disabled:opacity-45 disabled:cursor-not-allowed"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                {z.color ? (
                                  <span
                                    className="h-2.5 w-2.5 shrink-0 rounded-full border border-slate-300/80 dark:border-gray-600"
                                    style={{ backgroundColor: z.color }}
                                    aria-hidden
                                  />
                                ) : null}
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-slate-900 dark:text-gray-100 truncate">
                                    {z.name?.trim() || `Zone ${z.id.slice(0, 8)}…`}
                                  </div>
                                  <div className="text-[11px] text-slate-500 dark:text-gray-400 truncate">
                                    {z.campaign_name || 'Campaign'}
                                  </div>
                                </div>
                                <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400 shrink-0">
                                  {z.status}
                                </span>
                              </div>
                            </button>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {rows.length > 0 ? (
                    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-gray-400 px-1.5 pt-2 pb-0.5">
                      Campaigns
                    </p>
                  ) : null}
                  {rows.length > 0 ? (
                    <ul className="space-y-1 pb-1">
                      {rows.map((c, i) => (
                        <motion.li
                          key={c.id}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            type: 'spring',
                            damping: 26,
                            stiffness: 400,
                            delay: Math.min(i * 0.04, 0.32),
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              onOpenCampaign(c.id, null);
                              setOpen(false);
                            }}
                            className="w-full text-left rounded-xl px-3 py-2.5 text-sm bg-white/90 dark:bg-gray-900/70 hover:bg-white dark:hover:bg-gray-800/90 border border-slate-200/80 dark:border-gray-700/60 shadow-sm transition-colors active:scale-[0.99]"
                          >
                            <div className="font-medium text-slate-900 dark:text-gray-100 truncate">{c.name}</div>
                            <div className="text-[11px] text-slate-500 dark:text-gray-400 truncate flex justify-between gap-2 mt-0.5">
                              <span>{c.district_name || '—'}</span>
                              <span className="uppercase flex-shrink-0">{c.status}</span>
                            </div>
                          </button>
                        </motion.li>
                      ))}
                    </ul>
                  ) : null}
                </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
